/**
 * WORLD-CUP-MARKET-DOMINATION-WARROOM-P0
 *
 * Per-country, per-day metric aggregator powered by the `vs:event` bus
 * (emitted by `trackEvent`). Local-only, privacy-safe, no new tables, no
 * mocks. Source of truth for `/admin/market-domination`.
 */
import type { EventType } from "@/types";
import {
  SUPPORTED_COUNTRIES,
  type CountryCode,
  normalizeCountry,
} from "@/lib/country/countryExperienceEngine";
import { INFLUENCERS } from "@/lib/growth/influencerRegistry";

const STORE_KEY = "vs:market_dom_v1";
const COUNTRY_PREF_KEY = "vs:country_preference_v1";

export type MarketMetric =
  | "registered" | "active" | "predictions" | "shares" | "referrals"
  | "challenge_friend" | "daily_wow" | "hub_views" | "influencer_signups"
  | "broadcast_clicks" | "telegram" | "whatsapp" | "nation_selected"
  | "first_prediction_ms_sum" | "first_prediction_ms_n"
  | "first_share_ms_sum" | "first_share_ms_n"
  | "visits"
  | "f_visit" | "f_register" | "f_nation" | "f_prediction" | "f_share" | "f_referral" | "f_return"
  | "src_organic" | "src_influencer" | "src_referral" | "src_whatsapp" | "src_telegram" | "src_share";

export type AcquisitionSource =
  | "organic" | "influencer" | "referral" | "whatsapp" | "telegram" | "share";

type DailyBucket = Partial<Record<MarketMetric, number>>;
type CountryStore = Record<string, DailyBucket>;
type Store = Partial<Record<CountryCode, CountryStore>> & {
  influencerCounts?: Record<string, Partial<Record<MarketMetric, number>>>;
  firstSeen?: number;
  updatedAt?: number;
};

function emptyStore(): Store {
  return { influencerCounts: {}, firstSeen: Date.now(), updatedAt: Date.now() };
}

function readStore(): Store {
  if (typeof window === "undefined") return emptyStore();
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return emptyStore();
    return JSON.parse(raw) as Store;
  } catch { return emptyStore(); }
}

function writeStore(s: Store) {
  if (typeof window === "undefined") return;
  try {
    s.updatedAt = Date.now();
    window.localStorage.setItem(STORE_KEY, JSON.stringify(s));
  } catch { /* noop */ }
}

export function ymd(t: number = Date.now()): string {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function bump(country: CountryCode, metric: MarketMetric, by = 1) {
  const s = readStore();
  const cs = (s[country] ??= {});
  const day = ymd();
  const bucket = (cs[day] ??= {});
  bucket[metric] = (bucket[metric] ?? 0) + by;
  writeStore(s);
  notify();
}

function bumpInfluencer(slug: string, metric: MarketMetric, by = 1) {
  const s = readStore();
  s.influencerCounts ??= {};
  const cur = (s.influencerCounts[slug] ??= {});
  cur[metric] = (cur[metric] ?? 0) + by;
  writeStore(s);
}

function activeCountry(): CountryCode {
  if (typeof window === "undefined") return "BR";
  try {
    const c = normalizeCountry(window.localStorage.getItem(COUNTRY_PREF_KEY));
    if (c) return c;
  } catch { /* noop */ }
  return "BR";
}

function attributeCountry(payload: Record<string, unknown>): CountryCode {
  const direct = normalizeCountry(
    (payload.code as string | undefined) ?? (payload.country as string | undefined) ?? null,
  );
  if (direct) return direct;
  const slug = (payload.slug as string | undefined) ?? (payload.influencer as string | undefined);
  if (slug && INFLUENCERS[slug]) return INFLUENCERS[slug].country;
  return activeCountry();
}

type Mapping = (payload: Record<string, unknown>, country: CountryCode) => void;

const ROUTER: Partial<Record<EventType, Mapping>> = {
  app_open: (_p, c) => { bump(c, "active"); bump(c, "visits"); bump(c, "f_visit"); },
  country_hub_view: (_p, c) => bump(c, "hub_views"),
  country_experience_view: (_p, c) => bump(c, "visits"),
  country_broadcast_clicked: (_p, c) => bump(c, "broadcast_clicks"),
  registration_completed: (p, c) => {
    bump(c, "registered"); bump(c, "f_register");
    bump(c, sourceMetric(inferAcquisitionSource(p)));
  },
  nation_selected: (_p, c) => { bump(c, "nation_selected"); bump(c, "f_nation"); },
  first_prediction_completed: (p, c) => {
    bump(c, "predictions"); bump(c, "f_prediction");
    const ms = Number(p.timeToCompleteMs ?? p.ms ?? 0);
    if (Number.isFinite(ms) && ms > 0) {
      bump(c, "first_prediction_ms_sum", ms);
      bump(c, "first_prediction_ms_n");
    }
  },
  first_share_completed: (p, c) => {
    bump(c, "shares"); bump(c, "f_share");
    const ms = Number(p.timeToCompleteMs ?? p.ms ?? 0);
    if (Number.isFinite(ms) && ms > 0) {
      bump(c, "first_share_ms_sum", ms);
      bump(c, "first_share_ms_n");
    }
  },
  first_referral_sent: (_p, c) => { bump(c, "referrals"); bump(c, "f_referral"); },
  challenge_friend_sent: (_p, c) => bump(c, "challenge_friend"),
  daily_wow_shared: (_p, c) => { bump(c, "daily_wow"); bump(c, "shares"); },
  whatsapp_growth_cta_clicked: (_p, c) => { bump(c, "whatsapp"); bump(c, "shares"); },
  telegram_channel_cta_clicked: (_p, c) => bump(c, "telegram"),
  returned_same_day: (_p, c) => bump(c, "f_return"),
  influencer_page_view: (p, c) => {
    const slug = (p.slug as string) ?? "";
    if (slug) bumpInfluencer(slug, "hub_views");
    bump(c, "hub_views");
  },
  influencer_signup: (p, c) => {
    const slug = (p.slug as string) ?? "";
    if (slug) bumpInfluencer(slug, "registered");
    bump(c, "influencer_signups"); bump(c, "registered"); bump(c, "src_influencer");
  },
  influencer_referral: (p, c) => {
    const slug = (p.slug as string) ?? "";
    if (slug) bumpInfluencer(slug, "referrals");
    bump(c, "referrals");
  },
  influencer_share: (p, c) => {
    const slug = (p.slug as string) ?? "";
    if (slug) bumpInfluencer(slug, "shares");
    bump(c, "shares");
  },
  influencer_prediction: (p, c) => {
    const slug = (p.slug as string) ?? "";
    if (slug) bumpInfluencer(slug, "predictions");
    bump(c, "predictions");
  },
};

function inferAcquisitionSource(p: Record<string, unknown>): AcquisitionSource {
  const explicit = (p.source as string | undefined)?.toLowerCase();
  if (explicit === "influencer" || explicit === "referral" || explicit === "whatsapp" ||
      explicit === "telegram" || explicit === "share" || explicit === "organic") return explicit;
  if (p.influencer || p.slug) return "influencer";
  if (p.referralCode || p.ref) return "referral";
  return "organic";
}

function sourceMetric(src: AcquisitionSource): MarketMetric {
  switch (src) {
    case "organic": return "src_organic";
    case "influencer": return "src_influencer";
    case "referral": return "src_referral";
    case "whatsapp": return "src_whatsapp";
    case "telegram": return "src_telegram";
    case "share": return "src_share";
  }
}

let initialized = false;
const listeners = new Set<() => void>();
function notify() { for (const l of listeners) { try { l(); } catch { /* noop */ } } }

export function initMarketEngine() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("vs:event", (ev: Event) => {
    const detail = (ev as CustomEvent<{ event_type: EventType; event_data: Record<string, unknown> }>).detail;
    if (!detail) return;
    const handler = ROUTER[detail.event_type];
    if (!handler) return;
    const country = attributeCountry(detail.event_data ?? {});
    try { handler(detail.event_data ?? {}, country); } catch { /* noop */ }
  });
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function daysAgo(n: number): string[] {
  const out: string[] = [];
  const now = Date.now();
  for (let i = 0; i < n; i++) out.push(ymd(now - i * 86_400_000));
  return out;
}

export function sumMetric(country: CountryCode, metric: MarketMetric, days = 7): number {
  const s = readStore();
  const cs = s[country];
  if (!cs) return 0;
  let total = 0;
  for (const d of daysAgo(days)) total += cs[d]?.[metric] ?? 0;
  return total;
}

export function metricLastN(country: CountryCode, metric: MarketMetric, n: number): number[] {
  const s = readStore();
  const cs = s[country];
  const out: number[] = [];
  for (const d of daysAgo(n).slice().reverse()) out.push(cs?.[d]?.[metric] ?? 0);
  return out;
}

export interface CountryMetrics {
  country: CountryCode;
  registered7d: number; active7d: number; predictions7d: number;
  shares7d: number; referrals7d: number; challenges7d: number;
  dailyWow7d: number; hubViews7d: number; broadcastClicks7d: number;
  telegram7d: number; whatsapp7d: number; visits7d: number; nationSelected7d: number;
  avgTimeFirstPredictionSec: number | null;
  avgTimeFirstShareSec: number | null;
  acquisition: Record<AcquisitionSource, number>;
  funnel: { visit: number; register: number; nation: number; prediction: number; share: number; referral: number; return: number };
  retention: { d0: number; d1: number; d3: number; d7: number };
  growth: { d24: number; d72: number; d7: number };
  score: number;
  trend: "up" | "stable" | "down";
}

function avgMs(country: CountryCode, sumM: MarketMetric, nM: MarketMetric): number | null {
  const s = sumMetric(country, sumM, 7);
  const c = sumMetric(country, nM, 7);
  if (!c) return null;
  return Math.round(s / c / 1000);
}

function pctGrowth(country: CountryCode, hoursWindow: number): number {
  const days = Math.max(1, Math.round(hoursWindow / 24));
  const cur = sumMetric(country, "registered", days) + sumMetric(country, "active", days);
  const s = readStore();
  const cs = s[country];
  let prev = 0;
  if (cs) {
    const list = daysAgo(days * 2).slice(days);
    for (const d of list) prev += (cs[d]?.registered ?? 0) + (cs[d]?.active ?? 0);
  }
  if (!prev && !cur) return 0;
  if (!prev) return 100;
  return Math.round(((cur - prev) / prev) * 100);
}

function safeDiv(a: number, b: number): number { return b ? a / b : 0; }

function computeScore(m: {
  registered7d: number; active7d: number; shares7d: number; referrals7d: number;
  challenges7d: number; predictions7d: number; retention: { d7: number };
}): number {
  const growth = Math.min(1, (m.registered7d / 500) * 0.6 + (m.active7d / 1500) * 0.4);
  const virality = Math.min(1,
    (m.shares7d / 300) * 0.4 + (m.referrals7d / 150) * 0.4 + (m.challenges7d / 150) * 0.2);
  const retention = Math.min(1, m.retention.d7 / 25);
  const conversion = Math.min(1, safeDiv(m.predictions7d, m.registered7d));
  return Math.round(growth * 40 + virality * 30 + retention * 20 + conversion * 10);
}

function computeRetention(country: CountryCode): CountryMetrics["retention"] {
  const reg7 = sumMetric(country, "registered", 7) || 1;
  const active1 = sumMetric(country, "active", 1);
  const active3 = sumMetric(country, "active", 3);
  const active7 = sumMetric(country, "active", 7);
  return {
    d0: 100,
    d1: Math.min(100, Math.round((active1 / reg7) * 100)),
    d3: Math.min(100, Math.round((active3 / reg7) * 100)),
    d7: Math.min(100, Math.round((active7 / reg7) * 100)),
  };
}

export function getCountryMetrics(country: CountryCode): CountryMetrics {
  const acquisition: Record<AcquisitionSource, number> = {
    organic: sumMetric(country, "src_organic", 7),
    influencer: sumMetric(country, "src_influencer", 7),
    referral: sumMetric(country, "src_referral", 7),
    whatsapp: sumMetric(country, "src_whatsapp", 7),
    telegram: sumMetric(country, "src_telegram", 7),
    share: sumMetric(country, "src_share", 7),
  };
  const funnel = {
    visit: sumMetric(country, "f_visit", 7),
    register: sumMetric(country, "f_register", 7),
    nation: sumMetric(country, "f_nation", 7),
    prediction: sumMetric(country, "f_prediction", 7),
    share: sumMetric(country, "f_share", 7),
    referral: sumMetric(country, "f_referral", 7),
    return: sumMetric(country, "f_return", 7),
  };
  const base = {
    country,
    registered7d: sumMetric(country, "registered", 7),
    active7d: sumMetric(country, "active", 7),
    predictions7d: sumMetric(country, "predictions", 7),
    shares7d: sumMetric(country, "shares", 7),
    referrals7d: sumMetric(country, "referrals", 7),
    challenges7d: sumMetric(country, "challenge_friend", 7),
    dailyWow7d: sumMetric(country, "daily_wow", 7),
    hubViews7d: sumMetric(country, "hub_views", 7),
    broadcastClicks7d: sumMetric(country, "broadcast_clicks", 7),
    telegram7d: sumMetric(country, "telegram", 7),
    whatsapp7d: sumMetric(country, "whatsapp", 7),
    visits7d: sumMetric(country, "visits", 7),
    nationSelected7d: sumMetric(country, "nation_selected", 7),
    avgTimeFirstPredictionSec: avgMs(country, "first_prediction_ms_sum", "first_prediction_ms_n"),
    avgTimeFirstShareSec: avgMs(country, "first_share_ms_sum", "first_share_ms_n"),
    acquisition,
    funnel,
    retention: computeRetention(country),
    growth: { d24: pctGrowth(country, 24), d72: pctGrowth(country, 72), d7: pctGrowth(country, 168) },
  };
  const score = computeScore(base);
  const trend: CountryMetrics["trend"] =
    base.growth.d24 > 10 ? "up" : base.growth.d24 < -10 ? "down" : "stable";
  return { ...base, score, trend };
}

export function getAllCountryMetrics(): CountryMetrics[] {
  return SUPPORTED_COUNTRIES.map(getCountryMetrics).sort((a, b) => b.score - a.score);
}

export interface InvestmentRecommendation {
  ranking: { country: CountryCode; investmentScore: number }[];
  top: CountryCode;
  copy: string;
}

export function computeInvestmentRecommendation(budgetUsd = 10_000): InvestmentRecommendation {
  const metrics = getAllCountryMetrics();
  const scored = metrics.map((m) => {
    const growthScore = Math.min(100, Math.max(0, 50 + m.growth.d24));
    const viralityScore = Math.min(100, (m.shares7d + m.referrals7d * 2 + m.challenges7d) / 10);
    const retentionScore = m.retention.d7;
    const conversionScore = Math.min(100, safeDiv(m.predictions7d, m.registered7d) * 100);
    const investmentScore =
      growthScore * 0.4 + viralityScore * 0.3 + retentionScore * 0.2 + conversionScore * 0.1;
    return { country: m.country, investmentScore: Math.round(investmentScore) };
  });
  scored.sort((a, b) => b.investmentScore - a.investmentScore);
  const top = scored[0]?.country ?? "BR";
  const copy = `Si hoy tuvieras USD ${budgetUsd.toLocaleString("en-US")} de presupuesto, el mercado recomendado sería ${top}.`;
  return { ranking: scored, top, copy };
}

export interface ViralitySummary {
  country: CountryCode; shares: number; referrals: number; challenges: number; kFactor: number;
}

export function getViralitySummary(): ViralitySummary[] {
  return SUPPORTED_COUNTRIES.map((c) => {
    const shares = sumMetric(c, "shares", 7);
    const referrals = sumMetric(c, "referrals", 7);
    const challenges = sumMetric(c, "challenge_friend", 7);
    const registered = sumMetric(c, "registered", 7) || 1;
    const kFactor = Math.round(((referrals + challenges * 0.5) / registered) * 100) / 100;
    return { country: c, shares, referrals, challenges, kFactor };
  }).sort((a, b) => b.kFactor - a.kFactor);
}

export interface InfluencerRankRow {
  slug: string; country: CountryCode; displayName: string;
  views: number; signups: number; shares: number; referrals: number; predictions: number;
}

export function getInfluencerRanking(country?: CountryCode | null, limit = 10): InfluencerRankRow[] {
  const s = readStore();
  const counts = s.influencerCounts ?? {};
  const rows: InfluencerRankRow[] = Object.entries(INFLUENCERS)
    .filter(([, p]) => !country || p.country === country)
    .map(([slug, p]) => {
      const c = counts[slug] ?? {};
      return {
        slug, country: p.country, displayName: p.displayName,
        views: c.hub_views ?? 0,
        signups: c.registered ?? 0,
        shares: c.shares ?? 0,
        referrals: c.referrals ?? 0,
        predictions: c.predictions ?? 0,
      };
    });
  rows.sort((a, b) => (b.signups + b.referrals * 2) - (a.signups + a.referrals * 2));
  return rows.slice(0, limit);
}

const FUNNEL_ORDER = ["visit", "register", "nation", "prediction", "share", "referral", "return"] as const;
export type FunnelStepName = (typeof FUNNEL_ORDER)[number];

export interface FunnelDropoff { country: CountryCode; step: FunnelStepName; dropPct: number }

export function detectBottleneck(country: CountryCode): FunnelDropoff | null {
  const m = getCountryMetrics(country);
  const f = m.funnel;
  const seq = [f.visit, f.register, f.nation, f.prediction, f.share, f.referral, f.return];
  let worst: FunnelDropoff | null = null;
  for (let i = 1; i < seq.length; i++) {
    const prev = seq[i - 1]; const cur = seq[i];
    if (!prev) continue;
    const drop = Math.max(0, Math.round(((prev - cur) / prev) * 100));
    if (!worst || drop > worst.dropPct) worst = { country, step: FUNNEL_ORDER[i], dropPct: drop };
  }
  return worst;
}

export function clearMarketStore() {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(STORE_KEY); } catch { /* noop */ }
  notify();
}
